/**
 * Le Roomba (author's addition): thrown like a grenade, a vacuum cleaner with a face slides slowly about the map for
 * ROOMBA_TURNS turns. It looks ahead of it, in a fan of rays; when they meet something that is not solid (powder, liquid,
 * gas) a whirlwind opens in front of its mouth, like Kirby's, and it swallows it, cell by cell. As long as it sees such
 * matter it goes to and fro, left and right, after it; it does not enter a liquid or a powder, but waits until it has
 * eaten its way. Seeing none, it goes straight on, through the units, turns back only at a wall, and falls into any hole.
 * A ray stops at the first solid: it does not see through walls. A Wave that comes near it stops and is swallowed.
 * It is invincible: shots stop against it, nothing breaks it.
 */
import { Physique, STATE_LIQUID, STATE_STATIC, cosDeg, setCell, sinDeg, TRIG_ONE } from '@invaders/sim';
import { FP, FP_SHIFT, TICKS_PER_SECOND, type GameState, type Roomba, type Surge } from './state';

/** Its box, its last row on the ground. Values chosen, like all of these. */
export const ROOMBA_W = 14;
export const ROOMBA_H = 8;
const HALF = ROOMBA_W >> 1;
/** It lasts this many turns; in the sandbox, this many ticks. */
export const ROOMBA_TURNS = 3;
export const ROOMBA_SANDBOX_TICKS = 40 * TICKS_PER_SECOND;
/** A slow slide: 7.5 cells per second. */
export const ROOMBA_SPEED = FP >> 3;
const ROOMBA_STEP = 4;
const GRAVITY = 16;
const MAX_FALL = 6 * FP;
/**
 * How far it sees, and its fan of rays, every 10° around straight ahead. The fan sweeps by a degree per tick, so that no
 * cell stays between two rays; the whirlwind stays open for a whole sweep after the last thing seen, and it only turns
 * back once that sweep is over.
 */
export const ROOMBA_SIGHT = 26;
const FAN = 8;
const SWEEP = 10;
/** Cells it swallows per tick at most, 360 per second, shared by the rays that see something. */
const SWALLOW = 6;
/** Rows of the mouth above its last row. */
export const ROOMBA_MOUTH = 3;
/** A sound every so many ticks while it swallows. */
const INHALE_EVERY = 40;

const free = (state: GameState, x: number, y: number): boolean => {
  const { w, h, data } = state.world.physique;
  if (x < 0 || x >= w || y >= h) return false;
  return y < 0 || state.world.rules.materials.state[data[y * w + x]] >= STATE_LIQUID;
};

function fits(state: GameState, cx: number, by: number): boolean {
  for (let y = by - ROOMBA_H + 1; y <= by; y++) for (let x = cx - HALF; x < cx + HALF; x++) if (!free(state, x, y)) return false;
  return true;
}

/** A thrown Roomba lands at the last free cell (cx, cy), and goes on the way it flew. */
export function landRoomba(state: GameState, cx: number, cy: number, pvx: number, owner: number): void {
  let by = cy;
  for (let up = 0; up < 2 * ROOMBA_H && !fits(state, cx, by); up++) by--;
  state.roombas.push({ x: cx << FP_SHIFT, y: by << FP_SHIFT, vy: 0, dir: pvx < 0 ? -1 : 1, owner, turns: ROOMBA_TURNS, tick: 0, sucking: false, sight: 0, behind: false, ate: 0 });
}

/**
 * The fan of rays towards `dir`. Each stops at the first solid; the first cell that is not solid and not empty is what the
 * ray sees. Returns true if it saw anything. With `eat`, the rays swallow what they saw, SWALLOW cells at most; they take
 * turns (another first ray at each tick), so that it does not always eat on the same side.
 */
function look(state: GameState, r: Roomba, dir: number, eat: boolean): boolean {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const mx = (r.x >> FP_SHIFT) + dir * HALF;
  const my = (r.y >> FP_SHIFT) - ROOMBA_MOUTH;
  let seen = false;
  let left = eat ? SWALLOW : 0;
  const rays = 2 * FAN + 1;
  for (let i = 0; i < rays; i++) {
    const k = ((i + state.tick) % rays) - FAN;
    const angle = k * 10 + (state.tick % SWEEP) - (SWEEP >> 1);
    const cos = cosDeg(angle);
    const sin = sinDeg(angle);
    for (let d = 0; d <= ROOMBA_SIGHT; d++) {
      const x = mx + dir * Math.trunc((d * cos) / TRIG_ONE);
      const y = my - Math.trunc((d * sin) / TRIG_ONE);
      if (x < 0 || x >= w || y < 0 || y >= h) break;
      const m = data[y * w + x];
      if (matState[m] === STATE_STATIC) break;
      if (m === Physique.EMPTY) continue;
      seen = true;
      if (left > 0) { left--; setCell(state.world, x, y, Physique.EMPTY); r.ate = m; }
      break;
    }
  }
  return seen;
}

/** The Wave `s` is near the Roomba, at its height, with no wall between them. */
function waveInReach(state: GameState, r: Roomba, s: Surge): boolean {
  const { w, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const cx = r.x >> FP_SHIFT;
  const by = r.y >> FP_SHIFT;
  if (Math.abs(s.x - cx) > HALF + ROOMBA_SIGHT || s.floor - s.height > by || s.floor < by - ROOMBA_H) return false;
  const my = by - ROOMBA_MOUTH;
  for (let x = Math.min(cx, s.x); x <= Math.max(cx, s.x); x++) if (x >= 0 && x < w && matState[data[my * w + x]] === STATE_STATIC) return false;
  return true;
}

/** A Wave in reach stops and is swallowed (surge.ts shrinks it): the Roomba turns to it. */
function swallowWaves(state: GameState, r: Roomba): void {
  for (const s of state.surges) {
    if (s.eaten || !waveInReach(state, r, s)) continue;
    s.eaten = true;
    r.dir = s.x < (r.x >> FP_SHIFT) ? -1 : 1;
  }
  if (state.surges.some((s) => s.eaten && waveInReach(state, r, s))) { r.sight = SWEEP; r.ate = Physique.WATER; }
}

/** One tick of its slide on the ground: the fractions of a cell are in `r.x`. */
function slide(state: GameState, r: Roomba, cx: number, by: number): void {
  const { w, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const target = r.x + r.dir * ROOMBA_SPEED;
  if ((target >> FP_SHIFT) === cx) { r.x = target; return; }
  const next = cx + r.dir;
  const front = next + r.dir * HALF;
  if (next < HALF || next >= w - HALF) { r.dir = r.dir > 0 ? -1 : 1; return; }
  let up = 0;
  while (up <= ROOMBA_STEP && !fits(state, next, by - up)) up++;
  const row = by - up;
  let wet = false;
  if (up <= ROOMBA_STEP) for (let y = row - ROOMBA_H + 1; y <= row; y++) if (y >= 0 && matState[data[y * w + front]] === STATE_LIQUID) wet = true;
  if (up > ROOMBA_STEP || wet) {
    // A liquid or a powder ahead: it waits for what it swallows to go; a wall sends it back.
    let solid = false;
    for (let y = by - ROOMBA_H + 1; y <= by; y++) if (y >= 0 && matState[data[y * w + front]] === STATE_STATIC) solid = true;
    if (solid || !r.sucking) r.dir = r.dir > 0 ? -1 : 1;
    return;
  }
  // Down a step like up one; a deeper hole: it falls.
  let down = 0;
  while (down < ROOMBA_STEP && fits(state, next, row + down + 1)) down++;
  r.x = (next << FP_SHIFT) + (target & (FP - 1));
  r.y = (row + down) << FP_SHIFT;
}

export function updateRoombas(state: GameState): void {
  const matState = state.world.rules.materials.state;
  for (let i = state.roombas.length - 1; i >= 0; i--) {
    const r = state.roombas[i];
    const cx = r.x >> FP_SHIFT;
    let by = r.y >> FP_SHIFT;
    if (!fits(state, cx, by)) { by--; r.y -= FP; } // buried: it comes back up
    swallowWaves(state, r);
    // It remembers what it saw behind it, and goes back there once there is nothing left ahead: to and fro.
    if (look(state, r, -r.dir, false)) r.behind = true;
    if (look(state, r, r.dir, true)) r.sight = SWEEP;
    else if (r.sight > 0) r.sight--;
    else if (r.behind) { r.dir = r.dir > 0 ? -1 : 1; r.behind = false; r.sight = SWEEP; }
    r.sucking = r.sight > 0;
    if (r.sucking && state.tick % INHALE_EVERY === 0) state.events.push({ type: 'inhale', x: cx + r.dir * HALF, y: by - ROOMBA_MOUTH });
    if (fits(state, cx, by + 1)) {
      const { w, data } = state.world.physique;
      const wet = by >= HALF && matState[data[(by - HALF) * w + cx]] === STATE_LIQUID;
      r.vy = Math.min(wet ? FP : MAX_FALL, r.vy + Math.trunc(GRAVITY / state.map.gravityDiv));
      const target = r.y + r.vy;
      const last = target >> FP_SHIFT;
      while (by < last && fits(state, cx, by + 1)) by++;
      if (by === last) r.y = target;
      else { r.y = by << FP_SHIFT; r.vy = 0; }
    } else {
      r.vy = 0;
      slide(state, r, cx, by);
    }
    if (state.match === null && ++r.tick >= ROOMBA_SANDBOX_TICKS) state.roombas.splice(i, 1);
  }
}

/** Start of a turn (match): every Roomba has one turn less, and leaves after the last. */
export function roombasTurnStart(state: GameState): void {
  for (let i = state.roombas.length - 1; i >= 0; i--) if (--state.roombas[i].turns <= 0) state.roombas.splice(i, 1);
}

/** Index of the Roomba covering cell (x, y), or -1: shots stop against it. */
export function roombaAt(state: GameState, x: number, y: number): number {
  for (let i = 0; i < state.roombas.length; i++) {
    const r = state.roombas[i];
    const cx = r.x >> FP_SHIFT;
    const by = r.y >> FP_SHIFT;
    if (x >= cx - HALF && x < cx + HALF && y > by - ROOMBA_H && y <= by) return i;
  }
  return -1;
}
